import type { AuthType, LoginInfo } from "#src/api/user/types";

import { fetchLogin, fetchLogout } from "#src/api/user";
import { useAccessStore } from "#src/store/access";
import { useTabsStore } from "#src/store/tabs";
import { useUserStore } from "#src/store/user";
import { getAppNamespace } from "#src/utils/get-app-namespace";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const initialState = {
	token: "",
	refreshToken: "",
};

type AuthState = AuthType;

interface AuthAction {
	login: (loginPayload: LoginInfo) => Promise<void>
	logout: () => Promise<void>
	reset: () => void
};

export const useAuthStore = create<AuthState & AuthAction>()(

	persist((set, get) => ({
		...initialState,

		/**
		 * @en Handles the user login process: calls the API and updates the state with tokens.
		 */
		login: async (loginPayload) => {
			// @en Call the login API with username and password
			const response = await fetchLogin(loginPayload);

			// @en Update the state with the received token and refreshToken
			set({
				token: response.token,
				refreshToken: "",
			});

			// @en Set user info directly from login response
			if (response.user) {
				useUserStore.getState().setUserInfo({
					id: response.user.id.toString(),
					name: response.user.name,
					username: response.user.username,
					email: response.user.email,
					avatar: "",
					phoneNumber: "",
					description: "",
					roles: [],
				});
			}
		},

		logout: async () => {
			/**
			 * @en 1. Log out from the backend (API call)
			 */
			await fetchLogout();
			/**
			 * @en 2. Clear token and other authentication information
			 */
			get().reset();
		},

		reset: () => {
			/**
			 * @en Clear tokens (token and refreshToken)
			 */
			set({
				...initialState,
			});
			/**
			 * @en Clear user information (calling reset on the separate User Store)
			 * @see {@link https://github.com/pmndrs/zustand?tab=readme-ov-file#read-from-state-in-actions | Read from state in actions}
			 */
			useUserStore.getState().reset();

			/**
			 * @en Clear access/permission information (calling reset on the separate Access Store)
			 * @see https://github.com/pmndrs/zustand?tab=readme-ov-file#readingwriting-state-and-reacting-to-changes-outside-of-components
			 */
			useAccessStore.getState().reset();

			/**
			 * @en Clear open tabs (calling resetTabs on the separate Tabs Store)
			 */
			useTabsStore.getState().resetTabs();

			/**
			 * @en Clear keepAlive cache (This logic is handled by the container-layout component based on openTabs)
			 */
		},

	}), { name: getAppNamespace("access-token") }),

);
